import { StoreBusinessUsersModule } from "@/app/stores/business-users/store-business-users.module";
import { Component, inject, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Store } from "@ngrx/store";
import { HeaderComponent } from "./components/header/header.component";
import { MobileNavbarComponent } from "./components/mobile-navbar/mobile-navbar.component";
import { loadCurrentUser } from "@/app/stores/auth/auth.actions";
import { selectorCurrentUserStatus } from "@/app/stores/auth/auth.selectors";
import { KybBannerComponent } from "@/app/shared/components/kyb-banner/kyb-banner.component";
import { PermissionChecker } from "@/app/core/services/permission-checker.service";
import { Role } from "@/app/core/dtos/user.dto";
import { CommonModule } from "@angular/common";
import { loadAllConversationsByBusiness } from "@/app/stores/messages/messages.actions";
import { StoreMessagesModule } from "@/app/stores/messages/store-messages.module";
import { Helper } from "@/app/core/utils/helper";
import { KYCStatus } from "@/app/core/entities/business.entity";
import { selectorCurrentUser } from "@/app/stores/auth/auth.selectors";
import { selectorBeneficiary, selectorBeneficiaryStatus } from "@/app/stores/wallets/wallets.selectors";
import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { combineLatest, map, take } from "rxjs";

const UUID_V4ISH_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

@Component({
  selector: "app-main-layout",
  imports: [HeaderComponent, MobileNavbarComponent, RouterModule, StoreBusinessUsersModule, KybBannerComponent, CommonModule, StoreMessagesModule],
  templateUrl: "./main-layout.component.html",
})
export class MainLayoutComponent implements OnInit {
  readonly store = inject(Store);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly permissionChecker = inject(PermissionChecker);
  readonly currentRole = toSignal(this.permissionChecker.currentRole$, { initialValue: null as Role | null | undefined });

  readonly isMobileDevice = Helper.isMobileDevice();
  businessId = this.route.snapshot.params["businessId"];

  readonly needsBeneficiarySetup$ = combineLatest([
    this.store.select(selectorCurrentUser),
    this.store.select(selectorBeneficiary),
    this.store.select(selectorBeneficiaryStatus),
  ]).pipe(
    map(([user, beneficiary, beneficiaryStatus]) => {
      const routeBusinessId = this.businessId;
      const business =
        user?.businessUsers?.find((bu) => bu.businessId === routeBusinessId || bu.business?.id === routeBusinessId)?.business ??
        user?.businessUsers?.[0]?.business;
      const isKybActive = business?.kycStatus === KYCStatus.ACTIVE;
      const beneficiaryLoaded = beneficiaryStatus === ELoadingStatus.loaded;
      const hasBeneficiary = !!beneficiary?.payoutBeneficiaryId;
      return isKybActive && beneficiaryLoaded && !hasBeneficiary;
    })
  );
  readonly needsBeneficiarySetup = toSignal(this.needsBeneficiarySetup$, { initialValue: false });

  navItems = [
    {
      name: this.isMobileDevice ? "Home" : "Dashboard",
      icon: "/icons/nav/dashboard.svg",
      iconActive: "/icons/nav-active/dashboard.svg",
      route: `/${this.businessId}/dashboard`,
      roles: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER],
    },
  ];

  constructor() {
    document.getElementById("initial-loading").hidden = true;

    if (this.isMobileDevice) {
      this.navItems = [
        ...this.navItems,
        { name: "Projects", icon: "/icons/nav/projects.svg", iconActive: "/icons/nav-active/projects.svg", route: `/${this.businessId}/projects`, roles: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER] },
        { name: "Updates", icon: "/icons/nav/chats.svg", iconActive: "/icons/nav-active/chats.svg", route: `/${this.businessId}/updates`, roles: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER] },
        { name: "Wallets", icon: "/icons/nav/wallets.svg", iconActive: "/icons/nav-active/wallets.svg", route: `/${this.businessId}/wallets`, roles: [Role.ACCOUNT_MANAGER] },
        {
          name: "Accounting",
          icon: "/icons/nav/accounting.svg",
          iconActive: "/icons/nav-active/accounting.svg",
          route: `/${this.businessId}/settings/accounting`,
          roles: [Role.ACCOUNT_MANAGER],
        },
      ];
    } else {
      this.navItems = [
        ...this.navItems,
        { name: "Projects", icon: "/icons/nav/projects.svg", iconActive: "/icons/nav-active/projects.svg", route: `/${this.businessId}/projects`, roles: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER] },
        { name: "Updates", icon: "/icons/nav/chats.svg", iconActive: "/icons/nav-active/chats.svg", route: `/${this.businessId}/updates`, roles: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER] },
        { name: "Clients", icon: "/icons/nav/clients.svg", iconActive: "/icons/nav-active/clients.svg", route: `/${this.businessId}/clients`, roles: [Role.ACCOUNT_MANAGER, Role.PROJECT_MANAGER] },
        { name: "Wallets", icon: "/icons/nav/wallets.svg", iconActive: "/icons/nav-active/wallets.svg", route: `/${this.businessId}/wallets`, roles: [Role.ACCOUNT_MANAGER] },
        { name: "Team", icon: "/icons/nav/team.svg", iconActive: "/icons/nav-active/team.svg", route: `/${this.businessId}/team`, roles: [Role.ACCOUNT_MANAGER] },
        {
          name: "Accounting",
          icon: "/icons/nav/accounting.svg",
          iconActive: "/icons/nav-active/accounting.svg",
          route: `/${this.businessId}/settings/accounting`,
          roles: [Role.ACCOUNT_MANAGER],
        },
      ];
    }
  }

  ngOnInit() {
    this.store
      .select(selectorCurrentUserStatus)
      .pipe(take(1))
      .subscribe((status) => {
        if (status === ELoadingStatus.idle || status === ELoadingStatus.error) {
          this.store.dispatch(loadCurrentUser());
        }
      });

    if (this.businessId && UUID_V4ISH_REGEX.test(this.businessId)) {
      this.store.dispatch(
        loadAllConversationsByBusiness({
          businessId: this.businessId,
          query: {
            relations: ["project", "messages", "messages.request", "messages.response"],
            lastMessage: true,
          },
        })
      );
    }
  }
}
